import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Chip,
  Typography,
  Skeleton,
  InputAdornment,
  Tooltip,
  Paper
} from "@mui/material";
import {
  Search,
  Edit,
  Delete,
  PlayArrow,
  Visibility,
  PowerSettingsNew
} from "@mui/icons-material";
import { IN8NWorkflow } from "../../../../models/n8n-workflow";
import { IBasePaging } from "../../../../models/common/models.type";
import {
  calculateSuccessRate,
  formatLastExecuted,
  getWorkflowTypeLabel
} from "../../utils/helpers";
import { useDebounce } from "../../hooks/useDebounce";

interface WorkflowsTableProps {
  workflows: IN8NWorkflow[];
  loading: boolean;
  paging: IBasePaging;
  onPageChange: (page: number) => void;
  onView: (workflow: IN8NWorkflow) => void;
  onEdit: (workflow: IN8NWorkflow) => void;
  onDelete: (workflow: IN8NWorkflow) => void;
  onExecute: (workflow: IN8NWorkflow) => void;
  onActivate: (workflow: IN8NWorkflow, active: boolean) => void;
  onSearch: (query: string) => void;
}

const WorkflowsTable: React.FC<WorkflowsTableProps> = ({
  workflows,
  loading,
  paging,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onExecute,
  onActivate,
  onSearch
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);

  React.useEffect(() => {
    onSearch(debouncedSearch);
  }, [debouncedSearch, onSearch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // For now, we'll keep pageSize fixed at 10
    // You can implement this if needed
  };

  if (loading && workflows.length === 0) {
    return (
      <Box p={3}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Bar */}
      <Box px={2.5} py={2} pb={1.5}>
        <TextField
          size="small"
          placeholder="Search workflows..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#888", fontSize: 20 }} />
              </InputAdornment>
            )
          }}
          sx={{
            maxWidth: 320,
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              fontSize: "0.875rem",
              backgroundColor: "#f8f9fa",
              "& fieldset": { borderColor: "#e0e0e0" },
              "&:hover fieldset": { borderColor: "#05224e" },
              "&.Mui-focused fieldset": { borderColor: "#05224e" }
            }
          }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
              <TableCell sx={{ fontWeight: 600, color: "#05224e", fontSize: "0.8125rem", py: 1.5 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#05224e", fontSize: "0.8125rem", py: 1.5 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#05224e", fontSize: "0.8125rem", py: 1.5 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#05224e", fontSize: "0.8125rem", py: 1.5 }}>Last Execution</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#05224e", fontSize: "0.8125rem", py: 1.5 }}>Success Rate</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: "#05224e", fontSize: "0.8125rem", py: 1.5 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workflows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box py={4}>
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery
                        ? "No workflows found matching your search"
                        : "No workflows yet. Create your first workflow to get started!"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              workflows.map((workflow) => (
                <TableRow
                  key={workflow._id}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "#f8f9fa" },
                    "& td": { py: 1.25, fontSize: "0.8125rem" }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8125rem", color: "#333" }}>
                      {workflow.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                      {workflow.description}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={getWorkflowTypeLabel(workflow.type)}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: "#05224e",
                        color: "#05224e",
                        fontSize: "0.75rem",
                        height: 24
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={workflow.status === 'active' ? 'Active' : 'Inactive'}
                      size="small"
                      color={workflow.status === 'active' ? 'success' : 'default'}
                      sx={{ fontSize: "0.75rem", height: 24 }}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {formatLastExecuted(workflow.lastExecutedAt)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {calculateSuccessRate(
                        workflow.successfulExecutions,
                        workflow.totalExecutions
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {workflow.totalExecutions} total
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={0.25}>
                      <Tooltip title="View Details" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onView(workflow)}
                          sx={{ color: "#05224e", "&:hover": { backgroundColor: "rgba(5, 34, 78, 0.08)" } }}
                        >
                          <Visibility sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Execute" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onExecute(workflow)}
                          sx={{ color: "#1976d2", "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" } }}
                        >
                          <PlayArrow sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={workflow.status === 'active' ? 'Deactivate' : 'Activate'} arrow>
                        <IconButton
                          size="small"
                          onClick={() => onActivate(workflow, workflow.status !== 'active')}
                          sx={{
                            color: workflow.status === 'active' ? "#ed6c02" : "#2e7d32",
                            "&:hover": {
                              backgroundColor: workflow.status === 'active'
                                ? "rgba(237, 108, 2, 0.08)"
                                : "rgba(46, 125, 50, 0.08)"
                            }
                          }}
                        >
                          <PowerSettingsNew sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(workflow)}
                          sx={{ color: "#666", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                        >
                          <Edit sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(workflow)}
                          sx={{ color: "#d32f2f", "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.08)" } }}
                        >
                          <Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {workflows.length > 0 && (
        <TablePagination
          component="div"
          count={paging.totalCount}
          page={(paging.currentPage || 1) - 1}
          onPageChange={handleChangePage}
          rowsPerPage={paging.pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10]}
        />
      )}
    </Box>
  );
};

export default WorkflowsTable;